import React, {
  useEffect,
  useMemo,
  useState,
  Fragment,
  useRef,
  useLayoutEffect,
} from "react";
import "./SuburbsIndicatorsPage.css";
import { useGeographicUnits } from "../../../hooks/useGeographicUnits";
import { fetchGeographicUnitGroups } from "../../../api/infoApi";
import { useIndicatorRanges } from "../../../hooks/useIndicatorRanges";
import IndicatorsInfoTable from "./IndicatorsInfoTable";
import RightPanel from "./RightPanel";
import { useAuth } from "../../../context/AuthContext";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import templateBg from "../../../assets/images/insight_suburbs_template.png";

import portadaReporte from "../../../assets/images/portada_reporte_invescasa.png";
import pagina1Invescasa from "../../../assets/images/que_es_invescasa_pagina_1.png";
import pagina2Invescasa from "../../../assets/images/que_es_invescasa_pagina_2.png";

const indicators = [{ name: "Best suburbs rental yield" }];

const INDICATOR_META = [
  { key: "median_sales_price_last_12_months", title: "Median sales price (last 12m)", type: "currency" },
  { key: "median_price_change_5y_pct", title: "5 years median price % change", type: "percent" },
  { key: "median_price_change_10y_pct", title: "10 years median price % change", type: "percent" },
  { key: "lt_growth_rate_20y_pct", title: "LT growth rate (20y)", type: "percent" },
  { key: "rental_yield_pct", title: "Rental yield (annual)", type: "percent" },
];

export default function SuburbsIndicatorsPage() {
  const { places: cities, loading: loadingCities } = useGeographicUnits("City");
  const { membership } = useAuth();

  const pdfRef = useRef(null);

  const [selectedCity, setSelectedCity] = useState(null);
  const [isActiveMember, setIsActiveMember] = useState(false);

  const sortedCities = useMemo(() => [...(cities || [])].sort(), [cities]);

  useEffect(() => {
    if (!selectedCity && sortedCities.length > 0) setSelectedCity(sortedCities[0]);
  }, [sortedCities, selectedCity]);

  useEffect(() => {
    const status = membership?.status || "";
    const active = String(status).toLowerCase() === "active";
    setIsActiveMember(active);
  }, [membership]);

  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);

  const [rightPanel, setRightPanel] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingGroups(true);

    fetchGeographicUnitGroups("Suburb")
      .then((raw) => {
        if (cancelled) return;
        const valid = (Array.isArray(raw) ? raw : []).filter(
          (g) =>
            g?.data_level === "Suburb" &&
            typeof g?.parent_name === "string" &&
            Array.isArray(g?.data)
        );
        setGroups(valid);
      })
      .catch(() => !cancelled && setGroups([]))
      .finally(() => !cancelled && setLoadingGroups(false));

    return () => {
      cancelled = true;
    };
  }, []);

  const optionsForCity = useMemo(() => {
    if (!selectedCity) return [];
    const group = groups.find((g) => g.parent_name === selectedCity);
    return (group?.data || []).map(String);
  }, [groups, selectedCity]);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedSuburbs, setSelectedSuburbs] = useState([]);

  useEffect(() => {
    setSelectedSuburbs([]);
    setQuery("");
    setOpen(false);
  }, [selectedCity]);

  const filteredOptions = useMemo(() => {
    if (!query.trim()) return optionsForCity;
    const q = query.toLowerCase();
    return optionsForCity.filter((s) => s.toLowerCase().includes(q));
  }, [optionsForCity, query]);

  const allSelected =
  optionsForCity.length > 0 &&
  optionsForCity.every((s) => selectedSuburbs.includes(s));

  const onToggleAll = () => {
    setOpen(true);
    if (allSelected) {
      setSelectedSuburbs([]);
    } else {
      setSelectedSuburbs(optionsForCity);
    }
    setQuery("");
  };

  const onToggleOne = (name) => {
    setSelectedSuburbs((prev) =>
      prev.includes(name) ? prev.filter((s) => s !== name) : [...prev, name]
    );
  };

  const onRemoveChip = (name) => {
    setSelectedSuburbs((prev) => prev.filter((s) => s !== name));
  };

  const wrapRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const { ranges, loading: loadingRanges } = useIndicatorRanges(selectedCity);
  const [rangeFilters, setRangeFilters] = useState({});

  useEffect(() => {
    if (!ranges) return;
    const next = {};
    INDICATOR_META.forEach(({ key, type }) => {
      const r = ranges?.[key] || {};
      next[key] = {
        low: typeof r.min === "number" ? r.min : 0,
        high: typeof r.max === "number" ? r.max : 0,
        type,
      };
    });
    setRangeFilters(next);
  }, [ranges, selectedCity]);

  const fmtCurrency = (n) =>
    typeof n === "number"
      ? n.toLocaleString("en-AU", {
          style: "currency",
          currency: "AUD",
          maximumFractionDigits: 0,
        })
      : "-";

  const fmtPct = (n) => (typeof n === "number" ? `${n.toFixed(2)}%` : "-");

  const handleDownloadPDF = async () => {
    try {
      setIsGenerating(true);

      if (!pdfRef.current) return;

      const canvas = await html2canvas(pdfRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "px",
        format: [1920, 1080],
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const maxWidth = pageWidth * 0.56;
      const imgWidth = maxWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      const templateImg = new Image();
      templateImg.src = templateBg;
      templateImg.crossOrigin = "anonymous";
      await new Promise((resolve) => (templateImg.onload = resolve));

      const portada1 = new Image();
      portada1.src = portadaReporte;

      const portada2 = new Image();
      portada2.src = pagina1Invescasa;

      const portada3 = new Image();
      portada3.src = pagina2Invescasa;

      await Promise.all([
        portada1.decode?.() || new Promise((res) => (portada1.onload = res)),
        portada2.decode?.() || new Promise((res) => (portada2.onload = res)),
        portada3.decode?.() || new Promise((res) => (portada3.onload = res)),
      ]);

      pdf.addImage(portada1, "PNG", 0, 0, pageWidth, pageHeight);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(45);
      pdf.setTextColor("#ffffff");
      pdf.text("Suburbs indicators", 380, 685);
      pdf.addPage();

      pdf.addImage(portada2, "PNG", 0, 0, pageWidth, pageHeight);
      pdf.addPage();
      pdf.addImage(portada3, "PNG", 0, 0, pageWidth, pageHeight);
      pdf.addPage();

      pdf.addImage(templateImg, "PNG", 0, 0, pageWidth, pageHeight);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(125);
      pdf.setTextColor("#ffffff");
      pdf.text("Table of Contents", 450, 145);

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(38);
      pdf.setTextColor("#0f385d");

      const startX = 450;
      const baseY = 360;
      const stepY = 65;

      indicators.forEach((indicator, index) => {
        const itemNumber = index + 1;
        const positionY = baseY + stepY * (index + 1);
        pdf.text(`${itemNumber}. ${indicator.name}`, startX, positionY);
      });

      const metaStartY = baseY + stepY * (indicators.length + 2);
      pdf.setFont("helvetica", "bold");
      pdf.text("Indicator details:", startX, metaStartY);

      pdf.setFont("helvetica", "normal");
      INDICATOR_META.forEach((indicator, index) => {
        const metaY = metaStartY + stepY * (index + 1);
        pdf.text(`• ${indicator.title} (${indicator.type})`, startX + 20, metaY);
      });

      const barX = 1550;
      const barWidth = 11;
      const totalItems = indicators.length + INDICATOR_META.length + 2;
      const firstItemY = baseY - 38;
      const lastItemY = baseY + stepY * totalItems + 10;

      pdf.setFillColor("#0f385d");
      pdf.rect(barX, firstItemY, barWidth, lastItemY - firstItemY, "F");

      pdf.addPage();

      let heightLeft = imgHeight;

      while (heightLeft > 0) {
        pdf.addImage(templateImg, "PNG", 0, 0, pageWidth, pageHeight);

        const width = 1500;
        const height = 820;
        const posX = 280;
        const posY = 190;
        pdf.addImage(imgData, "PNG", posX, posY, width, height);

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(48);
        pdf.setTextColor("#ffffff");
        pdf.text("Real estate market insight suburbs", 435, 90);

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(32);
        pdf.setTextColor("#ffffff");
        pdf.text(
          "You can get a much more accurate and actionable understanding of where to buy,\nwhen to sell, and what to expect from your investment",
          435,
          130
        );

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(130);
        pdf.setTextColor("#000000");
        pdf.text(`1`, 54, 302);

        heightLeft -= pageHeight;
        if (heightLeft > 0) pdf.addPage();
      }

      pdf.save("suburbs-report.pdf");
    } catch (err) {
      console.error("Error generating PDF:", err);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="background">
      <div className="panel-modal-container pdf-root" ref={pdfRef}>
        {/* Cities menu (Suburbs in) */}
        <div className="mini-banner">
          <span className="banner-label">Suburbs in</span>
          <div className="tabs">
            {loadingCities && <span className="tabs__loading">Loading…</span>}
            {!loadingCities &&
              sortedCities.map((city, i) => (
                <Fragment key={city}>
                  {i > 0 && <div className="separator" />}
                  <button
                    className={`tab-button${selectedCity === city ? " active" : ""}`}
                    onClick={() => setSelectedCity(city)}
                    aria-pressed={selectedCity === city}
                    type="button"
                  >
                    {city}
                  </button>
                </Fragment>
              ))}
          </div>
        </div>

        {/* Suburbs indicators bar */}
        <div className="sib">
          <div className="sib__left">
            <h3 className="sib__title">Suburbs indicators</h3>

            <div className="sib__ms" ref={wrapRef}>
              <span className="sib__label">Suburbs</span>

              <div className={`sib__select ${open ? "open" : ""}`}>
                {/* 🔎 INPUT PRINCIPAL (reemplaza el placeholder) */}
                <div className="sib__chips">
                  <input
                    type="text"
                    className="sib__main-input"
                    placeholder="Select suburbs..."
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                  />

                  {allSelected ? (
                    <span className="sib__chip" key="__all__" title="All suburbs">
                      All{" "}
                      <button
                        className="sib__chip-x"
                        onClick={() => setSelectedSuburbs([])}
                        type="button"
                      >
                        ×
                      </button>
                    </span>
                  ) : (
                    selectedSuburbs.map((name) => (
                      <span className="sib__chip" key={name} title={name}>
                        {name}{" "}
                        <button
                          className="sib__chip-x"
                          onClick={() => onRemoveChip(name)}
                          type="button"
                        >
                          ×
                        </button>
                      </span>
                    ))
                  )}
                </div>

                <button
                  className={`sib__caret ${open ? "rot" : ""}`}
                  aria-label="Toggle suburbs dropdown"
                  onClick={() => setOpen((o) => !o)}
                  type="button"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {/* Dropdown panel */}
                {open && (
                  <div className="sib__dropdown">
                    <label className="sib__check-row sib__check-all">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={onToggleAll}
                        disabled={loadingGroups || optionsForCity.length === 0}
                      />
                      <span>Select all</span>
                    </label>

                    <div className="sib__list">
                      {loadingGroups && <div className="sib__hint">Loading suburbs…</div>}
                      {!loadingGroups && filteredOptions.length === 0 && (
                        <div className="sib__hint">No suburbs found</div>
                      )}

                      {!loadingGroups &&
                        filteredOptions.map((s) => {
                          const checked = selectedSuburbs.includes(s);
                          const label = `${s}, ${selectedCity}, Australia`;
                          return (
                            <label key={s} className="sib__check-row">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => onToggleOne(s)}
                              />
                              <span>{label}</span>
                            </label>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Download report */}
          <button
            className="sib__download"
            type="button"
            onClick={handleDownloadPDF}
            disabled={!isActiveMember || isGenerating}
            style={!isActiveMember || isGenerating ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
          >
            <span className="sib__download-ic">
              {isGenerating ? (
                <span className="spinner"></span>
              ) : (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              )}
            </span>

            {isGenerating ? "Generating..." : "Download report"}
          </button>
        </div>

        {/* Sliders */}
        <div className="sliders-wrap">
          {loadingRanges && <div className="sliders__loading">Loading ranges…</div>}

          {!loadingRanges && (
            <div className="sliders-row">
              {INDICATOR_META.map(({ key, title, type }) => {
                const r = ranges?.[key] || {};
                const min = typeof r.min === "number" ? r.min : 0;
                const max = typeof r.max === "number" ? r.max : 0;
                const cur = rangeFilters[key] || { low: min, high: max, type };
                return (
                  <IndicatorRangeCard
                    key={key}
                    title={title}
                    type={type}
                    min={min}
                    max={max}
                    fmtCurrency={fmtCurrency}
                    fmtPct={fmtPct}
                    valueLow={cur.low}
                    valueHigh={cur.high}
                    onChange={(low, high) =>
                      setRangeFilters((prev) => ({ ...prev, [key]: { low, high, type } }))
                    }
                    disabled={!isActiveMember}
                  />
                );
              })}
            </div>
          )}
        </div>

        <div className="si-two-col">
          <div className="si-two-col__main">
            <IndicatorsInfoTable
              city={selectedCity}
              selectedSuburbs={selectedSuburbs}
              rangeFilters={rangeFilters}
              onSelect={({ suburb, city }) => setRightPanel({ suburb, city })}
              selectedSuburb={rightPanel?.suburb || null}
              isActiveMember={isActiveMember}
            />
          </div>

          {rightPanel && (
            <RightPanel
              city={rightPanel.city}
              suburb={rightPanel.suburb}
              onClose={() => setRightPanel(null)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* Slider card */
function IndicatorRangeCard({
  title,
  type,
  min,
  max,
  fmtCurrency,
  fmtPct,
  valueLow,
  valueHigh,
  onChange,
  disabled = false,
}) {
  const [low, setLow] = useState(valueLow ?? min);
  const [high, setHigh] = useState(valueHigh ?? max);

  const sliderRef = useRef(null);
  const leftLabelRef = useRef(null);
  const rightLabelRef = useRef(null);
  const mergedLabelRef = useRef(null);

  const [dims, setDims] = useState({ w: 0, lw: 0, rw: 0, mw: 0 });
  const [mode, setMode] = useState("normal");

  useEffect(() => setLow(valueLow ?? min), [valueLow, min]);
  useEffect(() => setHigh(valueHigh ?? max), [valueHigh, max]);

  useLayoutEffect(() => {
    const measure = () => {
      const w = sliderRef.current?.offsetWidth || 0;
      const lw = leftLabelRef.current?.offsetWidth || 0;
      const rw = rightLabelRef.current?.offsetWidth || 0;
      const mw = mergedLabelRef.current?.offsetWidth || 0;

      setDims((d) => {
        if (d.w === w && d.lw === lw && d.rw === rw && d.mw === mw) return d;
        return { w, lw, rw, mw };
      });

      if (!w) return;

      const floor = min === max ? min - (type === "currency" ? 1 : 0.001) : min;
      const ceil = min === max ? max + (type === "currency" ? 1 : 0.001) : max;
      const pct = (v) => ((v - floor) / (ceil - floor)) * 100;

      const xL = (pct(low) / 100) * w;
      const xR = (pct(high) / 100) * w;

      const GAP = 8;
      const MERGE = 24;

      const rightEdgeLeft = xL + lw / 2;
      const leftEdgeRight = xR - rw / 2;
      const space = leftEdgeRight - rightEdgeLeft;

      if (xR - xL < MERGE) setMode("merged");
      else if (space < GAP) setMode("stacked");
      else setMode("normal");
    };

    measure();
    const raf = requestAnimationFrame(measure);

    const ro = new ResizeObserver(measure);
    if (sliderRef.current) ro.observe(sliderRef.current);
    if (leftLabelRef.current) ro.observe(leftLabelRef.current);
    if (rightLabelRef.current) ro.observe(rightLabelRef.current);
    if (mergedLabelRef.current) ro.observe(mergedLabelRef.current);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => measure()).catch(() => {});
    }

    const t = setTimeout(measure, 0);

    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [low, high, min, max, type]);

  const same = min === max;
  const floor = same ? min - (type === "currency" ? 1 : 0.001) : min;
  const ceil = same ? max + (type === "currency" ? 1 : 0.001) : max;

  const clampLow = (v) => Math.min(Math.max(v, floor), high);
  const clampHigh = (v) => Math.max(Math.min(v, ceil), low);

  const handleLow = (v) => {
    const val = clampLow(Number(v));
    setLow(val);
    onChange && onChange(val, high);
  };
  const handleHigh = (v) => {
    const val = clampHigh(Number(v));
    setHigh(val);
    onChange && onChange(low, val);
  };

  const format = (v) => (type === "currency" ? fmtCurrency(v) : fmtPct(v));
  const pct = (v) => ((v - floor) / (ceil - floor)) * 100;

  const clampLabelPct = (rawPct, labelW) => {
    if (!dims.w) return rawPct;
    const xPx = (rawPct / 100) * dims.w;
    const half = labelW / 2;
    const clampedPx = Math.max(half, Math.min(xPx, dims.w - half));
    return (clampedPx / dims.w) * 100;
  };

  const lowPctClamped = clampLabelPct(pct(low), dims.lw);
  const highPctClamped = clampLabelPct(pct(high), dims.rw);
  const mergedPctRaw = (pct(low) + pct(high)) / 2;
  const mergedPctClamped = clampLabelPct(mergedPctRaw, dims.mw);

  const labelTopStyle = { transform: "translate(-50%, -130%)" };
  const labelBottomStyle = { transform: "translate(-50%, 18px)" };
  const labelNormalStyle = { transform: "translate(-50%, -105%)" };

  return (
    <div className="slider-card" style={disabled ? { opacity: 0.5, pointerEvents: "none" } : undefined}>
      <div className="slider-card__title">{title}</div>

      <div className="slider" ref={sliderRef}>
        <div className="slider__range" style={{ left: `${pct(low)}%`, right: `${100 - pct(high)}%` }} />

        {/* Labels */}
        {mode !== "merged" && (
          <>
            <div
              ref={leftLabelRef}
              className="slider__value slider__value--left"
              style={{ left: `${lowPctClamped}%`, ...(mode === "stacked" ? labelTopStyle : labelNormalStyle) }}
            >
              {format(low)}
            </div>
            <div
              ref={rightLabelRef}
              className="slider__value slider__value--right"
              style={{ left: `${highPctClamped}%`, ...(mode === "stacked" ? labelBottomStyle : labelNormalStyle) }}
            >
              {format(high)}
            </div>
          </>
        )}

        {mode === "merged" && (
          <div
            ref={mergedLabelRef}
            className="slider__value slider__value--merged"
            style={{
              left: `${mergedPctClamped}%`,
              transform: "translate(-50%, -110%)",
              fontWeight: 400,
              maxWidth: "100%",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {format(low)} – {format(high)}
          </div>
        )}

        {/* Thumbs */}
        <input
          type="range"
          min={floor}
          max={ceil}
          step={type === "currency" ? 1000 : 0.01}
          value={low}
          onChange={(e) => handleLow(e.target.value)}
          className="slider__thumb slider__thumb--left"
          disabled={disabled}
        />
        <input
          type="range"
          min={floor}
          max={ceil}
          step={type === "currency" ? 1000 : 0.01}
          value={high}
          onChange={(e) => handleHigh(e.target.value)}
          className="slider__thumb slider__thumb--right"
          disabled={disabled}
        />
      </div>
    </div>
  );
}